import { useEffect, useMemo, useState } from 'react'
import { useOutletContext } from 'react-router-dom'

import StandingsRow from '../../components/StandingsRow'
import type { CurrentPlayerCtx } from '../../PlayerShell'
import { usePokerView } from '../PokerSeat/hooks/usePokerView'
import StandingsFilter, { type StackFilter } from './components/StandingsFilter'

/**
 * Stack — full-page standings list. The "field at a glance" view that
 * Bordet's compact StandingsBoard links into.
 *
 * Features:
 *  - Filter chips (Alle / Mitt bord / Slått ut)
 *  - Pinned-to-row scroll on first render — viewport scrolls to put the
 *    auth player's row visible without manual seeking
 *  - No 6-row cap — the whole tournament is visible
 *
 * Reuses StandingsRow so its appearance is byte-identical to the compact
 * version on Bordet.
 */
const Stack = () => {
  const { player } = useOutletContext<{ player: CurrentPlayerCtx }>()
  const { view, state } = usePokerView(player.polfEvent.usePokerPhase)
  const [filter, setFilter] = useState<StackFilter>('all')

  // Scroll auth player into view when data first lands or filter flips.
  // Looks the auth row up via `data-you="true"` so we don't need to
  // forward refs through StandingsRow. Done once per dependency change —
  // not on every 3s poll, so the player's scroll position isn't yanked
  // around mid-glance.
  useEffect(() => {
    if (
      state !== 'active' &&
      state !== 'paused' &&
      state !== 'eliminated' &&
      state !== 'winner'
    ) {
      return
    }
    const el = document.querySelector<HTMLLIElement>('[data-you="true"]')
    if (el) el.scrollIntoView({ behavior: 'auto', block: 'center' })
  }, [filter, state])

  const tableNumber = view?.seat?.tableNumber ?? null
  const tablemateIds = useMemo(() => {
    if (!view?.tableSeats?.length) return new Set<string>()
    return new Set(view.tableSeats.map((t) => t.id))
  }, [view?.tableSeats])

  if (state === 'no-poker') {
    return (
      <section className="px-6 py-12 text-center">
        <h1 className="font-display text-xl font-bold text-green-900 mb-2">
          Stack
        </h1>
        <p className="text-ink-500 text-sm">
          Pokerfase er ikke aktivert for denne turneringen.
        </p>
      </section>
    )
  }
  if (state === 'loading') {
    return (
      <section className="px-4 py-8 text-center text-ink-500">
        Laster stillingen…
      </section>
    )
  }
  if (state === 'error' || !view) {
    return (
      <section className="px-4 py-8 text-center text-flag">
        Kunne ikke laste stillingen.
      </section>
    )
  }
  if (state === 'pre-draw') {
    return (
      <section className="px-6 py-12 text-center">
        <h1 className="font-display text-xl font-bold text-green-900 mb-2">
          Stack
        </h1>
        <p className="text-ink-500 text-sm">
          Stillingen vises når dealeren har trukket seter og turneringen er i
          gang.
        </p>
      </section>
    )
  }

  const active = [...view.activeStandings].sort(
    (a, b) => b.currentStack - a.currentStack
  )
  const eliminated = [...view.eliminations].sort(
    (a, b) => a.finishingPosition - b.finishingPosition
  )
  const yourId = view.seat?.id

  // Filter to one of the three lenses. "my-table" needs the active row's
  // id to be in tableSeats (we only know tablemates by tableSeats — seats
  // don't carry tableNumber on the active-standing row).
  let visibleActive = active
  let visibleEliminated = eliminated
  if (filter === 'my-table') {
    visibleActive = active.filter((a) => tablemateIds.has(a.id))
    visibleEliminated = eliminated.filter((e) => tablemateIds.has(e.id))
  } else if (filter === 'eliminated') {
    visibleActive = []
  }

  const totalShown = visibleActive.length + visibleEliminated.length
  const totalPlayers = active.length + eliminated.length
  const tournamentOver = state === 'winner' || view.status === undefined

  return (
    <section className="pb-4">
      <div className="px-4 pt-4 pb-2 flex items-baseline justify-between">
        <h1 className="font-display text-xl font-bold text-green-900">
          {tournamentOver ? 'Sluttresultat' : 'Leaderboard'}
        </h1>
        <span className="text-xs text-ink-500 num">
          {active.length}/{totalPlayers} igjen
        </span>
      </div>

      <StandingsFilter
        value={filter}
        onChange={setFilter}
        hasTable={tableNumber != null}
        hasEliminated={eliminated.length > 0}
      />

      {totalShown === 0 ? (
        <div className="px-6 py-10 text-center text-sm text-ink-500">
          Ingen spillere i denne visningen.
        </div>
      ) : (
        <div className="mx-2 my-2 bg-paper rounded-lg shadow overflow-hidden">
          {visibleActive.length > 0 ? (
            <ol>
              {visibleActive.map((s, i) => (
                <StandingsRow
                  key={s.id}
                  playerName={s.playerName}
                  isYou={s.id === yourId}
                  variant={{
                    kind: 'active',
                    rank: i + 1,
                    currentStack: s.currentStack,
                  }}
                />
              ))}
            </ol>
          ) : null}

          {visibleEliminated.length > 0 && visibleActive.length > 0 ? (
            <div className="flex items-center gap-2 px-4 py-1.5 bg-cream-card border-t border-b border-ink-100">
              <span className="flex-1 h-px bg-ink-200" aria-hidden />
              <span className="up text-[10px] text-ink-500">Slått ut</span>
              <span className="flex-1 h-px bg-ink-200" aria-hidden />
            </div>
          ) : null}

          {visibleEliminated.length > 0 ? (
            <ol>
              {visibleEliminated.map((e) => (
                <StandingsRow
                  key={e.id}
                  playerName={e.playerName}
                  isYou={e.id === yourId}
                  variant={{
                    kind: 'eliminated',
                    finishingPosition: e.finishingPosition,
                  }}
                />
              ))}
            </ol>
          ) : null}
        </div>
      )}
    </section>
  )
}

export default Stack
